import { useState } from "react";
import { useNavigate } from "react-router-dom";
import GlassNav from "@/components/GlassNav";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PRESETS = [
  { key: "White Glass",     accent: "#C7A76C", bg: "#F5F6F8" },
  { key: "Champagne Glass", accent: "#C7A76C", bg: "#F7F3EC" },
  { key: "Ivory Luxury",    accent: "#B08B7A", bg: "#FBF7F4" },
  { key: "Pearl",           accent: "#A8A29E", bg: "#F4F4F5" },
  { key: "Soft Rose",       accent: "#D9808F", bg: "#FBF3F4" },
  { key: "Burgundy Glass",  accent: "#7A2E3A", bg: "#F5EEF0" },
  { key: "Emerald Glass",   accent: "#4C6B5A", bg: "#F0F5F1" },
];

export default function CreateWedding() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    partner_1: "", partner_2: "",
    display_format: "amp", custom_display: "",
    date: "", time: "",
    venue_name: "", venue_address: "", city: "", country: "",
    welcome_message: "Thank you for celebrating this special day with us.",
    upload_button_text: "Share Your Memories",
    wish_prompt: "Write a wish for the couple",
    cover_image: "",
    theme: { preset: "White Glass", accent_color: "#C7A76C", background_color: "#F5F6F8", glass_opacity: 0.55, glass_blur: 24, corner_radius: 24, heading_font: "Cormorant Garamond", body_font: "DM Sans", text_color: "#171717", secondary_text_color: "#737373" },
    slug: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setTheme = (patch) => setForm((f) => ({ ...f, theme: { ...f.theme, ...patch } }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.slug) delete payload.slug;
      const r = await api.post("/weddings", payload);
      toast.success("Wedding created");
      nav(`/dashboard/w/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create");
    } finally { setBusy(false); }
  };

  const display = (() => {
    if (form.display_format === "custom") return form.custom_display || `${form.partner_1} & ${form.partner_2}`;
    if (form.display_format === "plus") return `${form.partner_1} + ${form.partner_2}`;
    if (form.display_format === "and") return `${form.partner_1} and ${form.partner_2}`;
    return `${form.partner_1} & ${form.partner_2}`;
  })();

  return (
    <div className="min-h-screen relative">
      <GlassNav />
      <div className="pt-28 pb-24 px-4 max-w-3xl mx-auto">
        <div className="glass p-8 gloss-highlight" data-testid="wizard-panel">
          <div className="flex items-center gap-2 mb-6 text-xs uppercase tracking-widest text-neutral-500">
            {["Couple", "Date & venue", "Voice", "Theme"].map((s, i) => (
              <span key={s} className={i + 1 === step ? "text-neutral-900" : ""}>{i > 0 && "· "}{s}</span>
            ))}
          </div>

          {step === 1 && (
            <div className="space-y-4 fade-in">
              <h2 className="font-serif text-3xl">Who's getting married?</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <input className="glass-input rounded-full px-5 py-3 text-sm" placeholder="Partner 1"
                       value={form.partner_1} onChange={(e)=>set("partner_1", e.target.value)} data-testid="wiz-p1" />
                <input className="glass-input rounded-full px-5 py-3 text-sm" placeholder="Partner 2"
                       value={form.partner_2} onChange={(e)=>set("partner_2", e.target.value)} data-testid="wiz-p2" />
              </div>
              <div>
                <div className="text-xs text-neutral-500 mb-2 uppercase tracking-widest">Display format</div>
                <div className="flex flex-wrap gap-2">
                  {[["amp","& (default)"],["plus","+"],["and","and"],["custom","Custom"]].map(([k,label]) => (
                    <button key={k} onClick={()=>set("display_format", k)}
                      className={`px-4 py-2 rounded-full text-sm ${form.display_format===k?"btn-accent":"btn-ghost-glass"}`}
                      data-testid={`wiz-fmt-${k}`}>
                      {label}
                    </button>
                  ))}
                </div>
                {form.display_format === "custom" && (
                  <input className="glass-input rounded-full px-5 py-3 text-sm mt-3 w-full"
                         placeholder="e.g. Sarah loves John"
                         value={form.custom_display} onChange={(e)=>set("custom_display", e.target.value)} data-testid="wiz-custom-display" />
                )}
                <div className="mt-4 glass-subtle p-4 text-center">
                  <div className="text-xs text-neutral-500 uppercase tracking-widest">Preview</div>
                  <div className="font-serif text-3xl mt-1">{display || "Your names"}</div>
                </div>
              </div>
              <input className="glass-input rounded-full px-5 py-3 text-sm w-full" placeholder="Custom URL slug (optional, e.g. sarah-john)"
                     value={form.slug} onChange={(e)=>set("slug", e.target.value)} data-testid="wiz-slug" />
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4 fade-in">
              <h2 className="font-serif text-3xl">When & where</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <input type="date" className="glass-input rounded-full px-5 py-3 text-sm" value={form.date} onChange={(e)=>set("date", e.target.value)} data-testid="wiz-date" />
                <input type="time" className="glass-input rounded-full px-5 py-3 text-sm" value={form.time} onChange={(e)=>set("time", e.target.value)} data-testid="wiz-time" />
              </div>
              <input className="glass-input rounded-full px-5 py-3 text-sm w-full" placeholder="Venue name" value={form.venue_name} onChange={(e)=>set("venue_name", e.target.value)} data-testid="wiz-venue" />
              <input className="glass-input rounded-full px-5 py-3 text-sm w-full" placeholder="Address" value={form.venue_address} onChange={(e)=>set("venue_address", e.target.value)} data-testid="wiz-address" />
              <div className="grid sm:grid-cols-2 gap-3">
                <input className="glass-input rounded-full px-5 py-3 text-sm" placeholder="City" value={form.city} onChange={(e)=>set("city", e.target.value)} data-testid="wiz-city" />
                <input className="glass-input rounded-full px-5 py-3 text-sm" placeholder="Country" value={form.country} onChange={(e)=>set("country", e.target.value)} data-testid="wiz-country" />
              </div>
              <input className="glass-input rounded-full px-5 py-3 text-sm w-full" placeholder="Cover image URL (optional)" value={form.cover_image} onChange={(e)=>set("cover_image", e.target.value)} data-testid="wiz-cover" />
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4 fade-in">
              <h2 className="font-serif text-3xl">Your voice</h2>
              <div>
                <div className="text-xs text-neutral-500 uppercase tracking-widest mb-1">Welcome message</div>
                <textarea className="glass-input rounded-2xl px-5 py-3 text-sm w-full" rows={3} value={form.welcome_message} onChange={(e)=>set("welcome_message", e.target.value)} data-testid="wiz-welcome" />
              </div>
              <div>
                <div className="text-xs text-neutral-500 uppercase tracking-widest mb-1">Upload button text</div>
                <input className="glass-input rounded-full px-5 py-3 text-sm w-full" value={form.upload_button_text} onChange={(e)=>set("upload_button_text", e.target.value)} data-testid="wiz-uploadbtn" />
              </div>
              <div>
                <div className="text-xs text-neutral-500 uppercase tracking-widest mb-1">Wish prompt (shown to guests)</div>
                <input className="glass-input rounded-full px-5 py-3 text-sm w-full" value={form.wish_prompt} onChange={(e)=>set("wish_prompt", e.target.value)} data-testid="wiz-wishprompt" />
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4 fade-in">
              <h2 className="font-serif text-3xl">Choose a theme</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {PRESETS.map((p) => (
                  <button key={p.key} onClick={()=>setTheme({ preset: p.key, accent_color: p.accent, background_color: p.bg })}
                    className={`p-4 rounded-2xl border ${form.theme.preset===p.key?"ring-2 ring-offset-2":"border-white/70"} `}
                    style={{ background: p.bg, borderColor: form.theme.preset===p.key ? p.accent : undefined }}
                    data-testid={`wiz-preset-${p.key.replace(/\s+/g,'-').toLowerCase()}`}>
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full" style={{ background: p.accent }} />
                      <span className="text-sm">{p.key}</span>
                    </div>
                  </button>
                ))}
              </div>
              <div>
                <div className="text-xs text-neutral-500 uppercase tracking-widest mb-1">Accent color</div>
                <input type="color" value={form.theme.accent_color} onChange={(e)=>setTheme({ accent_color: e.target.value })}
                       className="w-16 h-10 rounded-lg border border-white/70" data-testid="wiz-accent" />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-8">
            <button onClick={()=>step>1?setStep(step-1):nav(-1)} className="btn-ghost-glass rounded-full px-5 py-2 text-sm" data-testid="wiz-back">
              {step===1?"Cancel":"Back"}
            </button>
            {step < 4 ? (
              <button onClick={()=>setStep(step+1)} disabled={step===1 && (!form.partner_1 || !form.partner_2)}
                      className="btn-accent rounded-full px-6 py-2 text-sm disabled:opacity-40" data-testid="wiz-next">
                Continue
              </button>
            ) : (
              <button onClick={submit} disabled={busy} className="btn-accent rounded-full px-6 py-2 text-sm" data-testid="wiz-submit">
                {busy?"Creating...":"Create wedding"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
